import { z } from 'zod';
import { PageQuerySchema } from './pagination.js';
import type { RunStatus } from './runs.js';

/*
 * Intake = what the client tells us about themselves: Atlas v2's run `context`
 * (company, links, departments with headcount) plus who gets the board. It is
 * required: no run starts without it. Unknown keys are kept, so a sender may
 * add contact details (see `contactFromIntake`).
 */

/** Atlas's department id (`ui.py` `dept_id`): `Customer Experience` → `customer-experience`. */
export function departmentIdOf(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export const ContextDepartmentSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  description: z.string().trim().min(1, 'Description is required').max(2000),
  people: z.int('Headcount is a whole number').min(1, 'At least 1 person'),
});
export type ContextDepartment = z.infer<typeof ContextDepartmentSchema>;

/** Atlas `Context` (`ui.py`), with its rules, so a bad form fails at review, not at start. */
export const ContextSchema = z.object({
  company_name: z.string().trim().min(1, 'Company name is required').max(200),
  urls: z
    .record(
      z.string().trim().min(1).max(100),
      z.url({ protocol: /^https?$/, error: 'Enter a full http(s) URL' }).max(500),
    )
    .default({}),
  departments: z
    .array(ContextDepartmentSchema)
    .min(1, 'Add at least one department')
    .max(12, 'At most 12 departments')
    .superRefine((ds, ctx) => {
      const seen = new Set<string>();
      ds.forEach((d, i) => {
        const id = departmentIdOf(d.name);
        if (!id)
          ctx.addIssue({ code: 'custom', path: [i, 'name'], message: 'Needs a letter or digit' });
        else if (seen.has(id))
          ctx.addIssue({
            code: 'custom',
            path: [i, 'name'],
            message: `Another department already has the id ${id}`,
          });
        seen.add(id);
      });
    }),
});
export type Context = z.infer<typeof ContextSchema>;

export const IntakeSchema = z.looseObject({
  viewer_email: z.string().trim().max(320).default(''),
  context: ContextSchema,
});
export type Intake = z.infer<typeof IntakeSchema>;

export const PAYMENT_TAGS = ['unpaid', 'paid', 'cancelled'] as const;
export type PaymentTag = (typeof PAYMENT_TAGS)[number];
export const PAYMENT_LABELS: Record<PaymentTag, string> = {
  unpaid: 'Not yet paid',
  paid: 'Paid',
  cancelled: 'Cancelled',
};

/** Every contact field may be empty until the intake provides it or staff fill it in. */
export const ContactSchema = z.object({
  name: z.string().trim().max(200).nullable(),
  email: z.email('Enter a valid email').max(320).nullable(),
  phone: z.string().trim().max(50).nullable(),
  company: z.string().trim().max(200).nullable(),
  jobTitle: z.string().trim().max(200).nullable(),
});
export type Contact = z.infer<typeof ContactSchema>;

export const UpdateContactSchema = ContactSchema.partial();
export type UpdateContact = z.infer<typeof UpdateContactSchema>;

/** A form entered by hand (admins, developers) for a custom run. */
export const CreateFormSchema = z.object({
  intake: IntakeSchema,
  contact: UpdateContactSchema.optional(),
});
export type CreateForm = z.infer<typeof CreateFormSchema>;

export const SetPaymentSchema = z.object({ tag: z.enum(PAYMENT_TAGS) });
export const ReviewSchema = z.object({ notes: z.string().trim().max(4000).optional() });
export const UpdateIntakeSchema = z.object({ payload: IntakeSchema });

export const FormsQuerySchema = z
  .object({
    payment: z.enum(PAYMENT_TAGS).optional(),
    reviewed: z.enum(['yes', 'no']).optional(),
    hasRun: z.enum(['yes', 'no']).optional(),
    q: z.string().trim().max(200).optional(),
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
  })
  .extend(PageQuerySchema.shape);
export type FormsQuery = z.infer<typeof FormsQuerySchema>;

export interface StaffRef {
  id: string;
  name: string;
}

export interface FormSummary {
  id: string;
  receivedAt: string;
  company: string;
  industry: string;
  contact: { name: string | null; email: string | null };
  paymentTag: PaymentTag;
  /** `manual` when entered by staff for a custom run. */
  source: 'intake' | 'manual';
  intakeReviewed: boolean;
  intakeReviewedBy: StaffRef | null;
  run: { id: string; status: RunStatus } | null;
}

export interface FormDetail extends FormSummary {
  /**
   * What will be sent to Atlas: staff edits if any, otherwise the original.
   * Null when it is not a complete v2 intake (a form from before Atlas v2).
   */
  intake: Intake | null;
  /** What will be sent, as stored, whatever its shape. */
  rawIntake: unknown;
  /** Exactly as received; never changed. May be a v1 intake. */
  originalIntake: unknown;
  edited: boolean;
  contactDetail: Contact & { source: 'intake' | 'staff'; updatedAt: string };
  paymentTagSetBy: StaffRef | null;
  paymentTagSetAt: string | null;
  intakeReviewedAt: string | null;
  intakeReviewNotes: string | null;
  /** Who entered a manual form. */
  createdBy: StaffRef | null;
  /** Set once a run has started: the intake was sent to Atlas and is read only. */
  lockedAt: string | null;
}

export interface HistoryEntry {
  id: string;
  at: string;
  actor: { id: string; name: string; role: string } | null;
  action: string;
  before: unknown;
  after: unknown;
  meta: unknown;
}

/** "dispatch and logistics" → "Dispatch and logistics". */
export function capitalizeFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
